import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import {MedicalAPI} from "@/api/medical";
import {PlayerAPI} from "@/api/player";
import {loadPlayerRealStats} from "@/utils/computePlayerStats";
import {computePlayerSummary} from "@/utils/playerIndices";

import "./index.scss";

// ── Component ─────────────────────────────────────────────────────────────────

export default function PlayerIndices({playerData, valuationsData, isInjured}) {
	const season = useSelector((state) => state?.season);
	const seasonId = useSelector((state) => state?.season?.id);
	const playerId = playerData?.id;

	const [tqrRpeData, setTqrRpeData] = useState({avgTQR: null, avgRPE: null});
	const [injuries, setInjuries] = useState([]);
	const [realStats, setRealStats] = useState(null);

	useEffect(() => {
		if (!playerData?.id) return;

		const weekAgo = moment().subtract(7, "days").format("YYYY-MM-DD");
		const today = moment().format("YYYY-MM-DD");

		Promise.all([
			PlayerAPI.playerListTQR(playerData.id, {
				fromDate: weekAgo,
				toDate: today,
			}).catch(() => []),
			PlayerAPI.listRPE({
				playerId: playerData.id,
				fromDate: weekAgo,
				toDate: today,
			}).catch(() => []),
			MedicalAPI.recordsList({playerId: playerData.id}).catch(() => []),
		]).then(([tqrData, rpeData, medicalRes]) => {
			const parseTqr = (v) => {
				if (v == null) return null;
				if (typeof v === "string" && v.indexOf("-") !== -1)
					v = v.split(" - ")[0];
				const p = parseFloat(v);
				return isNaN(p) ? null : p;
			};

			const tqrValues = (tqrData || [])
				.map((t) => parseTqr(t.tqrValue))
				.filter((v) => v !== null);
			const rpeValues = (rpeData || [])
				.map((r) => parseFloat(r.rpe))
				.filter((v) => !isNaN(v));
			const avgTQR = tqrValues.length
				? tqrValues.reduce((s, v) => s + v, 0) / tqrValues.length
				: null;
			const avgRPE = rpeValues.length
				? rpeValues.reduce((s, v) => s + v, 0) / rpeValues.length
				: null;

			setTqrRpeData({avgTQR, avgRPE});

			const allInjuries = (medicalRes || [])
				.filter((r) => Array.isArray(r.injuriesIllness))
				.flatMap((r) => r.injuriesIllness.filter((i) => !i.recordType));
			setInjuries(allInjuries);
		});
	}, [playerData?.id]);

	useEffect(() => {
		let active = true;
		setRealStats(null);
		if (playerId && seasonId) {
			loadPlayerRealStats({id: playerId}, seasonId).then((stats) => {
				if (active) setRealStats(stats);
			});
		}
		return () => {
			active = false;
		};
	}, [playerId, seasonId]);

	const summary = useMemo(() => {
		return computePlayerSummary({
			playerData,
			avgTQR: tqrRpeData.avgTQR,
			avgRPE: tqrRpeData.avgRPE,
			isInjured: isInjured || false,
			injuries,
			valuations: valuationsData,
			realStats,
			seasonStart: season?.startDate,
			seasonEnd: season?.endDate,
		});
	}, [
		playerData,
		tqrRpeData,
		isInjured,
		injuries,
		valuationsData,
		realStats,
		season,
	]);

	const indices = [
		summary.readiness && {
			label: "Prontezza",
			value: summary.readiness.value,
			sublabel: summary.readiness.label,
			color: summary.readiness.color,
		},
		summary.performance && {
			label: "Performance",
			value: summary.performance.value,
			sublabel:
				summary.performance.label +
				(summary.performance.type === "goalkeeper" ? " (POR)" : ""),
			color: summary.performance.color,
		},
		summary.availability && {
			label: "Disponibilità",
			value: summary.availability.value,
			sublabel:
				summary.availability.daysLost > 0
					? `${summary.availability.daysLost}gg persi`
					: summary.availability.label,
			color: summary.availability.color,
			suffix: "%",
		},
	].filter(Boolean);

	const performanceValue = (metric) => {
		const format = (value, decimals = 2) =>
			Number(value).toLocaleString("it-IT", {maximumFractionDigits: decimals});
		if (summary.performance.type === "goalkeeper")
			return `${format(metric.value * 100, 1)}%`;
		if (metric.code === "PRES")
			return `${metric.value}/${realStats.outfield.matchesAvailable}`;
		if (metric.code === "MIN/G") return `${format(metric.value, 1)} min`;
		if (metric.code === "DISC")
			return `${format(metric.value)} cartellini ponderati/90′`;
		return format(metric.value);
	};

	const hasRealData = Boolean(realStats?.outfield);

	if (!indices.length && !hasRealData && !realStats?.error) return null;

	return (
		<div className="pi-root">
			<div className="pi-overview">
				<div className="pi-header">
					<span className="pi-header__title">Indici giocatore</span>
					{hasRealData && (
						<span className="pi-header__source">
							Stagione {realStats.outfield.profile.seasonName}
						</span>
					)}
				</div>
				{indices.length > 0 && (
					<div className="pi-indices">
						{indices.map((idx) => (
							<div
								key={idx.label}
								className={`pi-index pi-index--${idx.color}`}
							>
								<div className="pi-index__number">
									<span className="pi-index__value">
										{idx.value != null ? idx.value : "—"}
									</span>
									{idx.value != null && (
										<span className="pi-index__suffix">
											{idx.suffix || "/100"}
										</span>
									)}
								</div>
								<div className="pi-index__description">
									<span className="pi-index__label">{idx.label}</span>
									<span className="pi-index__sublabel">{idx.sublabel}</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
			{realStats?.error && <p className="pi-data-note">{realStats.error}</p>}

			{realStats?.goalkeeper?.incomplete && (
				<p>Report portiere parziali o da verificare.</p>
			)}
			{/* ── Performance breakdown ────────────────────────────────── */}
			{summary.performance?.details?.length > 0 && (
				<details className="pi-breakdown">
					<summary className="pi-breakdown__title">
						Dettaglio Performance
						{summary.performance.type === "goalkeeper" && " (da report gara)"}
					</summary>
					<p className="pi-data-note">
						Valori reali; le barre indicano il punteggio su 100. Riferimento:
						intera stagione selezionata.
					</p>
					<div className="pi-breakdown__bars">
						{summary.performance.details.map((d, i) => (
							<div key={i} className="pi-bar">
								<div className="pi-bar__header">
									<span className="pi-bar__label">{d.label || d.code}</span>
									<span className="pi-bar__values">
										<span className="pi-bar__value">{performanceValue(d)}</span>
										<span className="pi-bar__score">
											Punteggio {d.normalized}/100
										</span>
									</span>
								</div>
								<div className="pi-bar__track">
									<div
										className={`pi-bar__fill pi-bar__fill--${d.normalized >= 65 ? "green" : d.normalized >= 35 ? "yellow" : "red"}`}
										style={{width: `${d.normalized}%`}}
									/>
								</div>
							</div>
						))}
					</div>
				</details>
			)}
		</div>
	);
}
